import { useEffect, useState } from 'react'

import logger from '@/client/utils/logger'
import useAiSearchStore from '@/stores/aiSearchStore'
import useSearchSelectorStore from '@/stores/searchSelectorStore'

import type { UseSearchStateSyncProps } from '../../SearchBox.types'

/**
 * Search state synchronization hook
 * Manages synchronization between different search states and input values
 */
export const useSearchStateSync = ({
	autocomplete,
	inputRef,
	query,
	searchType,
	refinementList,
	refine,
	status,
	nbHits,
}: UseSearchStateSyncProps): void => {
	const [isInitialRender, setIsInitialRender] = useState(true)
	const {
		aiSearchResults,
		searchByPrompt,
		prefetchedSearchByAudio,
		setAiSearchPending,
	} = useAiSearchStore()
	const inputDraft = useSearchSelectorStore((state) => state.inputDraft)

	// Sync input value with search states
	useEffect(() => {
		if (!inputRef.current) return

		try {
			if (aiSearchResults?.length > 0 && query.length > 0) {
				refine('')
			}

			const displayValue =
				prefetchedSearchByAudio?.title ??
				searchByPrompt ??
				(query || inputDraft)

			inputRef.current.value = displayValue
			autocomplete.setQuery(displayValue)
		} catch (error) {
			logger('Error syncing search state', error, {
				query,
				searchByPrompt: searchByPrompt || '',
				prefetchedSearchByAudio: String(!!prefetchedSearchByAudio),
			})
		}
	}, [
		aiSearchResults,
		autocomplete,
		inputDraft,
		inputRef,
		prefetchedSearchByAudio,
		query,
		refinementList,
		refine,
		searchType,
		searchByPrompt,
	])

	// Handle AI search pending state
	useEffect(() => {
		try {
			if (
				!isInitialRender &&
				status === 'idle' &&
				query.length > 0 &&
				!prefetchedSearchByAudio
			) {
				setAiSearchPending(false)
			}

			setIsInitialRender(false)
		} catch (error) {
			logger('Error managing AI search pending state', error, {
				isInitialRender: String(isInitialRender),
				status,
				query,
				prefetchedSearchByAudio: String(!!prefetchedSearchByAudio),
			})
		}
	}, [
		isInitialRender,
		status,
		query,
		nbHits,
		prefetchedSearchByAudio,
		setAiSearchPending,
	])
}
